import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, GraduationCap, Phone, Users, Loader2, ChevronDown, ChevronRight, Trophy, Sparkles, Ban, Pencil, Trash2, Check, X, Plus, UserPlus, MessageSquareText, MoveRight, AlertTriangle } from "lucide-react";
import { familyLabel } from "@/lib/utils";
import { toast } from "sonner";

function ClassChip({ c, dropped = false, onDrop, onOpenClass }) {
  const isComp = (c.program || "").toLowerCase() === "competitive";
  const clickable = !!onOpenClass && !!c.class_id;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] font-medium group ${
        dropped
          ? "bg-slate-50 border-slate-200 text-slate-500 line-through"
          : isComp
          ? "bg-amber-50 border-amber-200 text-amber-900"
          : "bg-red-50 border-red-100 text-[#8A1620]"
      } ${clickable ? "cursor-pointer hover:ring-2 hover:ring-[#D9232E]/30" : ""}`}
      title={`${c.class_name}${c.day ? ` · ${c.day}s` : ""}${c.time ? ` · ${c.time}` : ""}${c.instructor ? ` · ${c.instructor}` : ""}${clickable ? " — click to open class roster" : ""}`}
      onClick={clickable ? (e) => { e.stopPropagation(); onOpenClass(c.class_id); } : undefined}
      data-testid={`class-chip-${c.class_id}`}
    >
      {isComp ? <Trophy className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
      <span className="truncate max-w-[240px]">{c.class_name}</span>
      {c.day && <span className="text-slate-400">· {c.day.slice(0, 3)}</span>}
      {c.location_code && (
        <span className="ml-0.5 px-1 rounded bg-white border border-slate-200 text-slate-500 text-[9px] font-mono">
          {c.location_code === "Victoria" ? "VIC" : c.location_code}
        </span>
      )}
      {onDrop && !dropped && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDrop(c); }}
          className="ml-1 -mr-0.5 p-0.5 rounded hover:bg-white/70 text-slate-500 hover:text-[#D9232E] opacity-60 group-hover:opacity-100 transition-opacity"
          data-testid={`class-drop-${c.enrollment_id}`}
          title="Drop this class"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function AddClassPicker({ student, classes, onEnroll, onClose }) {
  const [q, setQ] = useState("");
  const [enrolling, setEnrolling] = useState(null);
  const takenIds = new Set([...(student.active_classes || []).map((c) => c.class_id), ...(student.dropped_classes || []).map((c) => c.class_id)]);
  const activeIds = new Set((student.active_classes || []).map((c) => c.class_id));
  const droppedIds = new Set((student.dropped_classes || []).map((c) => c.class_id));

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (classes || [])
      .filter((c) => !activeIds.has(c.id)) // hide already-active
      .filter((c) => {
        if (!needle) return true;
        return (
          (c.name || "").toLowerCase().includes(needle) ||
          (c.day || "").toLowerCase().includes(needle) ||
          (c.instructor || "").toLowerCase().includes(needle) ||
          (c.location_code || "").toLowerCase().includes(needle)
        );
      })
      .slice(0, 40);
  }, [classes, q, activeIds]);

  const pick = async (c) => {
    setEnrolling(c.id);
    try { await onEnroll(c); } finally { setEnrolling(null); }
  };

  return (
    <div className="mt-2 border border-red-200 rounded-md bg-white p-3" onClick={(e) => e.stopPropagation()} data-testid={`add-class-picker-${student.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Add class</div>
        <button type="button" onClick={onClose} className="ml-auto p-1 text-slate-400 hover:text-[#D9232E]" data-testid={`add-class-close-${student.id}`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <Input
          autoFocus
          placeholder="Search class name, day, instructor..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-8 h-8 text-xs"
          data-testid={`add-class-search-${student.id}`}
        />
      </div>
      <div className="mt-2 max-h-56 overflow-y-auto border border-slate-100 rounded">
        {filtered.length === 0 ? (
          <div className="text-xs italic text-slate-400 p-3 text-center">No matching classes.</div>
        ) : (
          filtered.map((c) => {
            const wasDropped = droppedIds.has(c.id);
            const isBusy = enrolling === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c)}
                disabled={isBusy}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-red-50 text-left border-b border-slate-50 last:border-b-0 disabled:opacity-50"
                data-testid={`add-class-option-${c.id}`}
              >
                <Plus className="w-3.5 h-3.5 text-[#D9232E] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {c.day || "—"} {c.time ? `· ${c.time}` : ""} {c.instructor ? `· ${c.instructor}` : ""} {c.location_code ? `· ${c.location_code}` : ""}
                  </div>
                </div>
                {wasDropped && <span className="text-[9px] text-blue-600 font-semibold shrink-0">Reactivate</span>}
                {isBusy && <Loader2 className="w-3 h-3 animate-spin" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function StudentRow({ s, expanded, onToggle, onRename, onDelete, onEnroll, onDrop, onOpenClass, onText, onMove, onEditPhone, siblingSuspects, classes }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name || "");
  const [saving, setSaving] = useState(false);
  const [addingClass, setAddingClass] = useState(false);
  const activeCount = s.active_classes?.length || 0;
  const droppedCount = s.dropped_classes?.length || 0;
  const primaryPhone = s.phones?.[0]?.phone;
  const primaryContactId = s.phones?.[0]?.contact_id;
  const primaryOptedOut = !!s.phones?.[0]?.opted_out;
  const noClasses = activeCount === 0 && droppedCount === 0;

  const save = async (e) => {
    e?.stopPropagation();
    const trimmed = name.trim();
    if (!trimmed || trimmed === s.name) { setEditing(false); return; }
    setSaving(true);
    try { await onRename(s, trimmed); setEditing(false); }
    finally { setSaving(false); }
  };
  const remove = async (e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete ${s.name}? This removes all their class enrollments and history. This cannot be undone.`)) return;
    await onDelete(s);
  };

  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`student-row-${s.id}`}>
      <div className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left">
        <button type="button" onClick={onToggle} className="w-8 h-8 rounded-full bg-red-50 text-[#D9232E] flex items-center justify-center shrink-0" data-testid={`student-toggle-${s.id}`}>
          <GraduationCap className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onToggle}>
          <div className="flex items-center gap-2 flex-wrap">
            {editing ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Enter") save(e); if (e.key === "Escape") { setName(s.name); setEditing(false); } }}
                onBlur={save}
                disabled={saving}
                className="text-sm font-semibold text-slate-900 border border-red-300 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-[#D9232E]"
                data-testid={`student-name-input-${s.id}`}
              />
            ) : (
              primaryContactId ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onText?.(primaryContactId, primaryPhone); }}
                  className="font-semibold text-slate-900 text-sm truncate hover:text-[#D9232E] hover:underline text-left"
                  data-testid={`student-name-link-${s.id}`}
                  title={`Open chat with ${s.name}${primaryPhone ? ` (${primaryPhone})` : ""}`}
                >
                  {s.name || "(unnamed)"}
                </button>
              ) : (
                <span className="font-semibold text-slate-900 text-sm truncate">{s.name || "(unnamed)"}</span>
              )
            )}
            {activeCount > 0 && (
              <Badge variant="secondary" className="text-[10px] bg-red-50 text-[#8A1620] border border-red-100">
                {activeCount} class{activeCount === 1 ? "" : "es"}
              </Badge>
            )}
            {droppedCount > 0 && (
              <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200">
                {droppedCount} dropped
              </Badge>
            )}
            {noClasses && (
              <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                No classes
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 truncate">
            {primaryPhone && (
              <span className="inline-flex items-center gap-1 truncate">
                <Phone className="w-3 h-3" /> {primaryPhone}
                {s.phones.length > 1 && (
                  <span className="ml-1 text-[10px] text-slate-400">+{s.phones.length - 1}</span>
                )}
              </span>
            )}
            {!primaryPhone && <span className="italic text-amber-600">No phone on file</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {editing ? (
            <>
              <button type="button" onClick={save} disabled={saving} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`student-save-${s.id}`} title="Save">
                <Check className="w-4 h-4" />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setName(s.name); setEditing(false); }} className="p-1 text-slate-500 hover:bg-slate-100 rounded" data-testid={`student-cancel-${s.id}`} title="Cancel">
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              {primaryContactId && !primaryOptedOut && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onText?.(primaryContactId, primaryPhone); }}
                  className="p-1 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded"
                  data-testid={`student-text-${s.id}`}
                  title={`Text ${primaryPhone || "parent"}`}
                >
                  <MessageSquareText className="w-4 h-4" />
                </button>
              )}
              <button type="button" onClick={(e) => { e.stopPropagation(); onMove?.(s); }} className="p-1 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded" data-testid={`student-move-${s.id}`} title="Move to a different family (change parent phone)">
                <MoveRight className="w-4 h-4" />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }} className="p-1 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded" data-testid={`student-edit-${s.id}`} title="Rename">
                <Pencil className="w-4 h-4" />
              </button>
              <button type="button" onClick={remove} className="p-1 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded" data-testid={`student-delete-${s.id}`} title="Delete student">
                <Trash2 className="w-4 h-4" />
              </button>
              <button type="button" onClick={onToggle} className="p-1 text-slate-400" data-testid={`student-expand-${s.id}`}>
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 bg-slate-50/50 border-t border-slate-100" data-testid={`student-detail-${s.id}`}>
          {siblingSuspects && siblingSuspects.length > 0 && (
            <div className="mb-3 p-2.5 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid={`sibling-suspect-${s.id}`}>
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-amber-900">Possible sibling{siblingSuspects.length > 1 ? "s" : ""} on a different phone:</div>
                <div className="text-[11px] text-amber-800 mt-0.5">{siblingSuspects.map(x => x.name).join(", ")}. If they belong together, click <MoveRight className="w-3 h-3 inline align-text-bottom" /> to move this student onto their family's phone.</div>
              </div>
            </div>
          )}
          {s.phones?.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">Phones on file</div>
              <div className="flex flex-wrap gap-1.5">
                {s.phones.map((p) => (
                  <div key={p.contact_id} className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded border text-[11px] ${p.opted_out ? "bg-slate-100 border-slate-200 text-slate-400 line-through" : "bg-white border-slate-200 text-slate-700"}`}>
                    <Phone className="w-3 h-3" /> {p.phone}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onEditPhone?.(p); }}
                      className="ml-1 p-1 rounded hover:bg-red-50 text-slate-500 hover:text-[#D9232E]"
                      data-testid={`student-edit-phone-${p.contact_id}`}
                      title="Fix this number (updates it for every student on it)"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    {p.opted_out ? (
                      <Ban className="w-3 h-3 ml-0.5" />
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onText?.(p.contact_id, p.phone); }}
                        className="ml-1 p-1 rounded hover:bg-red-50 text-slate-500 hover:text-[#D9232E]"
                        data-testid={`student-text-phone-${p.contact_id}`}
                        title={`Text ${p.phone}`}
                      >
                        <MessageSquareText className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
                Active classes ({activeCount})
              </div>
              <button
                type="button"
                onClick={() => setAddingClass((v) => !v)}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#D9232E] hover:text-[#B91C25]"
                data-testid={`add-class-button-${s.id}`}
              >
                <Plus className="w-3 h-3" /> {addingClass ? "Cancel" : "Add class"}
              </button>
            </div>
            {activeCount === 0 && !addingClass ? (
              <div className="text-xs italic text-slate-400">Not currently enrolled in any class.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {s.active_classes.map((c) => (
                  <ClassChip key={c.enrollment_id} c={c} onDrop={onDrop} onOpenClass={onOpenClass} />
                ))}
              </div>
            )}
            {addingClass && (
              <AddClassPicker
                student={s}
                classes={classes}
                onEnroll={async (c) => {
                  await onEnroll(s, c);
                  setAddingClass(false);
                }}
                onClose={() => setAddingClass(false)}
              />
            )}
          </div>

          {droppedCount > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">
                Dropped ({droppedCount})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {s.dropped_classes.map((c) => (
                  <ClassChip key={c.enrollment_id} c={c} dropped onOpenClass={onOpenClass} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FamilyCard({ family, onText, children }) {
  const phones = [...new Map(family.members.flatMap((m) => m.phones || []).map((p) => [p.contact_id, p])).values()];
  const active = family.members.reduce((n, m) => n + (m.active_classes?.length || 0), 0);
  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`family-card-${family.key}`}>
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50/80" data-testid={`family-header-${family.key}`}>
        <div className="w-8 h-8 rounded-full bg-[#D9232E] text-white flex items-center justify-center shrink-0"><Users className="w-4 h-4" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {phones[0] ? (
              <button type="button" onClick={(e) => { e.stopPropagation(); onText?.(phones[0].contact_id, phones[0].phone); }}
                className="font-semibold text-slate-900 text-sm hover:text-[#D9232E] hover:underline text-left" data-testid={`family-name-link-${family.key}`}>
                {family.label}
              </button>
            ) : <span className="font-semibold text-slate-900 text-sm">{family.label}</span>}
            <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700">{family.members.length} kids</Badge>
            {active > 0 && <Badge variant="secondary" className="text-[10px] bg-red-50 text-[#8A1620] border border-red-100">{active} class{active === 1 ? "" : "es"}</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
            <Phone className="w-3 h-3" /> {phones.map((p) => p.phone).join(" · ") || "No phone"}
          </div>
        </div>
      </div>
      <div className="pl-6 border-t border-slate-100" data-testid={`family-members-${family.key}`}>{children}</div>
    </div>
  );
}

function NewStudentDialog({ open, onOpenChange, classes, contacts, onCreated }) {
  const [name, setName] = useState("");
  const [contactId, setContactId] = useState("");
  const [q, setQ] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newFirst, setNewFirst] = useState("");
  const [newLast, setNewLast] = useState("");
  const [pickedClasses, setPickedClasses] = useState(new Set());
  const [classSearch, setClassSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setName(""); setContactId(""); setQ(""); setNewPhone(""); setNewFirst(""); setNewLast("");
      setPickedClasses(new Set()); setClassSearch("");
    }
  }, [open]);

  const matchedContacts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return (contacts || []).filter((c) =>
      `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(needle) ||
      (c.phone || "").includes(needle)
    ).slice(0, 8);
  }, [contacts, q]);

  const pickedContact = useMemo(() => contactId ? (contacts || []).find((c) => c.id === contactId) : null, [contactId, contacts]);

  const filteredClasses = useMemo(() => {
    const needle = classSearch.trim().toLowerCase();
    if (!needle) return (classes || []).slice(0, 60);
    return (classes || []).filter((c) =>
      (c.name || "").toLowerCase().includes(needle) ||
      (c.day || "").toLowerCase().includes(needle) ||
      (c.instructor || "").toLowerCase().includes(needle) ||
      (c.location_code || "").toLowerCase().includes(needle)
    ).slice(0, 60);
  }, [classes, classSearch]);

  const toggleClass = (id) => {
    setPickedClasses((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Enter the student's name"); return; }
    const digits = (newPhone || "").replace(/\D/g, "");
    if (!contactId && digits.length < 10) { toast.error("Pick a family or enter a 10-digit phone"); return; }
    setSaving(true);
    try {
      const payload = {
        name: trimmed,
        class_ids: Array.from(pickedClasses),
        ...(contactId ? { contact_id: contactId } : {
          parent_phone: newPhone.trim(),
          parent_first_name: newFirst.trim(),
          parent_last_name: newLast.trim(),
        }),
      };
      const { data } = await api.post("/students", payload);
      toast.success(`${trimmed} added${data.enrollments_created ? ` — enrolled in ${data.enrollments_created} class(es)` : ""}`);
      onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't create student"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-student-dialog">
        <DialogHeader>
          <DialogTitle>Add new student</DialogTitle>
          <p className="text-sm text-slate-500">Add a student, tie them to a family, and (optionally) enroll them in one or more classes.</p>
        </DialogHeader>

        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Student name*</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="First Last" className="mt-1 h-10" data-testid="new-student-name" />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Parent / family*</div>
            {pickedContact ? (
              <div className="flex items-center gap-2 border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2">
                <Check className="w-4 h-4 text-emerald-700" />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-slate-900">{`${pickedContact.first_name || ""} ${pickedContact.last_name || ""}`.trim() || pickedContact.phone}</div>
                  <div className="text-xs text-slate-500">{pickedContact.phone}</div>
                </div>
                <button type="button" onClick={() => { setContactId(""); setQ(""); }} className="text-xs text-slate-500 hover:text-[#D9232E]" data-testid="new-student-clear-family">Change</button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search existing family by name or phone..." className="pl-9 h-10" data-testid="new-student-family-search" />
                </div>
                {matchedContacts.length > 0 && (
                  <div className="mt-1 border border-slate-200 rounded-md max-h-40 overflow-y-auto bg-white">
                    {matchedContacts.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setContactId(c.id); setQ(""); }}
                        className="w-full text-left px-3 py-2 hover:bg-red-50 border-b border-slate-50 last:border-b-0"
                        data-testid={`new-student-family-${c.id}`}
                      >
                        <div className="text-sm font-medium">{`${c.first_name || ""} ${c.last_name || ""}`.trim() || "(no name)"}</div>
                        <div className="text-xs text-slate-500">{c.phone}</div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-[11px] uppercase font-semibold text-slate-600 mb-2">Or add a new family</div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Phone*" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} className="h-9" data-testid="new-student-new-phone" />
                    <Input placeholder="First name" value={newFirst} onChange={(e) => setNewFirst(e.target.value)} className="h-9" data-testid="new-student-new-first" />
                    <Input placeholder="Last name" value={newLast} onChange={(e) => setNewLast(e.target.value)} className="h-9" data-testid="new-student-new-last" />
                  </div>
                </div>
              </>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Classes (optional)</div>
              <div className="text-[10px] text-slate-500">{pickedClasses.size} selected</div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={classSearch} onChange={(e) => setClassSearch(e.target.value)} placeholder="Search class name, day, instructor..." className="pl-9 h-10" data-testid="new-student-class-search" />
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-md bg-white">
              {filteredClasses.length === 0 ? (
                <div className="text-xs italic text-slate-400 p-3 text-center">No classes match.</div>
              ) : (
                filteredClasses.map((c) => {
                  const on = pickedClasses.has(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleClass(c.id)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left border-b border-slate-50 last:border-b-0 ${on ? "bg-red-50" : "hover:bg-slate-50"}`}
                      data-testid={`new-student-class-${c.id}`}
                    >
                      <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${on ? "bg-[#D9232E] border-[#D9232E]" : "border-slate-300"}`}>
                        {on && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-900 truncate">{c.name}</div>
                        <div className="text-[10px] text-slate-500">
                          {c.day || "—"} {c.time ? `· ${c.time}` : ""} {c.instructor ? `· ${c.instructor}` : ""} {c.location_code ? `· ${c.location_code}` : ""}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !name.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="new-student-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create student"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Students() {
  // Hydrate from sessionStorage so switching tabs shows the list INSTANTLY;
  // then refresh in the background. Feels dramatically faster than blank + spinner.
  const cachedStudents = (() => { try { return JSON.parse(sessionStorage.getItem("sts:students") || "null") || []; } catch { return []; } })();
  const cachedClasses  = (() => { try { return JSON.parse(sessionStorage.getItem("sts:classes")  || "null") || []; } catch { return []; } })();
  const [students, setStudents] = useState(cachedStudents);
  const [classes, setClasses] = useState(cachedClasses);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(cachedStudents.length === 0);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());
  const [newOpen, setNewOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null); // student being moved
  const [movePhone, setMovePhone] = useState("");
  const [moveSaving, setMoveSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();

  const load = async () => {
    try {
      // Only show the spinner if we have NOTHING cached — otherwise the cached
      // list stays on screen while we quietly refresh in the background.
      if (students.length === 0) setLoading(true);
      // Drop /contacts from initial load (only NewStudentDialog uses it; it lazy-loads).
      const [stRes, clRes] = await Promise.all([
        api.get("/students"),
        api.get("/classes"),
      ]);
      const st = Array.isArray(stRes.data) ? stRes.data : [];
      const cl = Array.isArray(clRes.data) ? clRes.data : [];
      setStudents(st);
      setClasses(cl);
      try { sessionStorage.setItem("sts:students", JSON.stringify(st)); } catch {}
      try { sessionStorage.setItem("sts:classes",  JSON.stringify(cl)); } catch {}
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load students"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-expand & scroll to a specific student when linked with ?open=<student_id>
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || loading || students.length === 0) return;
    const exists = students.some((s) => s.id === openId);
    if (!exists) return;
    setExpanded((prev) => {
      const n = new Set(prev);
      n.add(openId);
      return n;
    });
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="student-row-${openId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }, [searchParams, loading, students]);

  const openClass = (classId) => {
    if (!classId) return;
    nav(`/classes?open=${encodeURIComponent(classId)}`);
  };

  const renameStudent = async (s, newName) => {
    try {
      await api.put(`/students/${s.id}`, { name: newName });
      setStudents((prev) => prev.map((x) => x.id === s.id ? { ...x, name: newName } : x));
      toast.success(`Renamed to ${newName}`);
    } catch (e) { toast.error(formatApiError(e, "Rename failed")); }
  };

  const deleteStudent = async (s) => {
    try {
      await api.delete(`/students/${s.id}`);
      setStudents((prev) => prev.filter((x) => x.id !== s.id));
      toast.success(`Deleted ${s.name}`);
    } catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };

  const enrollInClass = async (s, cls) => {
    try {
      await api.post("/enrollments", { student_id: s.id, class_id: cls.id });
      toast.success(`${s.name} → ${cls.name}`);
      await load();
    } catch (e) { toast.error(formatApiError(e, "Couldn't enroll")); }
  };

  const dropFromClass = async (s, c) => {
    if (!window.confirm(`Drop ${s.name} from "${c.class_name}"?`)) return;
    try {
      await api.delete(`/enrollments/${c.enrollment_id}`);
      toast.success(`Dropped from ${c.class_name}`);
      await load();
    } catch (e) { toast.error(formatApiError(e, "Couldn't drop")); }
  };

  const openTextForContact = async (contactId, phone) => {
    if (!contactId) { toast.error("No phone on file to text"); return; }
    try {
      const { data } = await api.post("/conversations/by-contact", { contact_id: contactId });
      nav(`/inbox/${data.conversation_id}`);
    } catch (e) { toast.error(formatApiError(e, "Couldn't open thread")); }
  };

  const openMove = (student) => {
    setMoveTarget(student);
    setMovePhone("");
  };

  const editPhone = async (p) => {
    const next = window.prompt(`Fix number ${p.phone}\nThis updates it for every student on this number.`, p.phone);
    if (!next || next.trim() === p.phone) return;
    const send = (merge) => api.post(`/contacts/${p.contact_id}/change-phone`, { phone: next.trim(), merge });
    try {
      let res;
      try { res = await send(false); }
      catch (e) {
        if (e?.response?.status !== 409) throw e;
        if (!window.confirm(`${e.response.data?.detail}\n\nOK = put these students on that number together (texts and history are combined).`)) return;
        res = await send(true);
      }
      toast.success(res.data.merged ? `Families merged on ${res.data.phone}` : `Number updated to ${res.data.phone}`);
      load();
    } catch (e) { toast.error(formatApiError(e, "Couldn't update number")); }
  };

  const submitMove = async () => {
    if (!moveTarget) return;
    const phone = movePhone.trim();
    if (!phone) return toast.error("Enter the parent phone");
    setMoveSaving(true);
    try {
      const { data } = await api.post(`/students/${moveTarget.id}/reassign-contact`, { new_phone: phone });
      toast.success(`${moveTarget.name} moved to ${data.new_contact_phone}`);
      setMoveTarget(null);
      setMovePhone("");
      load();
    } catch (e) { toast.error(formatApiError(e, "Couldn't move student")); }
    finally { setMoveSaving(false); }
  };

  // Sibling auto-detect: same last name, DIFFERENT parent contact groups.
  // Only surfaces suspects where each side is a lone student on their contact
  // (avoids nagging on true multi-kid families that already share a phone).
  const suspectMap = (() => {
    const byLast = {};
    for (const s of students) {
      const parts = (s.name || "").trim().split(/\s+/);
      const last = (parts[parts.length - 1] || "").toLowerCase();
      if (!last || last.length < 3) continue;
      byLast[last] = byLast[last] || [];
      byLast[last].push(s);
    }
    const out = {};
    for (const last of Object.keys(byLast)) {
      const grp = byLast[last];
      if (grp.length < 2 || grp.length > 6) continue; // too big → probably not real siblings
      const contactKeys = new Set(grp.map(s => (s.contact_ids || []).slice().sort().join("|")));
      if (contactKeys.size < 2) continue; // already on the same family → not a suspect
      for (const s of grp) {
        const mine = (s.contact_ids || []).slice().sort().join("|");
        const others = grp.filter(g => g.id !== s.id && (g.contact_ids || []).slice().sort().join("|") !== mine);
        if (others.length) out[s.id] = others;
      }
    }
    return out;
  })();

  const toggle = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const families = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = (s) => {
      const active = s.active_classes?.length || 0;
      const dropped = s.dropped_classes?.length || 0;
      const phones = s.phones?.length || 0;
      if (filter === "active" && active === 0) return false;
      if (filter === "dropped_only" && !(active === 0 && dropped > 0)) return false;
      if (filter === "no_classes" && (active > 0 || dropped > 0)) return false;
      if (filter === "no_phone" && phones > 0) return false;
      if (!needle) return true;
      if ((s.name || "").toLowerCase().includes(needle)) return true;
      if ((s.phones || []).some((p) => (p.phone || "").toLowerCase().includes(needle))) return true;
      if ((s.active_classes || []).some((c) => (c.class_name || "").toLowerCase().includes(needle))) return true;
      if ((s.dropped_classes || []).some((c) => (c.class_name || "").toLowerCase().includes(needle))) return true;
      return false;
    };
    // Siblings = students sharing any parent number (union-find on contact ids)
    const parent = {};
    const find = (x) => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
    for (const s of students) {
      parent[s.id] = parent[s.id] || s.id;
      for (const p of s.phones || []) {
        const k = `c:${p.contact_id}`;
        parent[k] = parent[k] || k;
        parent[find(k)] = find(s.id);
      }
    }
    const groups = {};
    for (const s of students) { (groups[find(s.id)] = groups[find(s.id)] || []).push(s); }
    return Object.entries(groups)
      .filter(([, members]) => members.some(matches))
      .map(([key, members]) => ({ key, members: members.sort((a, b) => (a.name || "").localeCompare(b.name || "")), label: familyLabel(members.map((m) => m.name)) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [students, q, filter]);
  const filtered = useMemo(() => families.flatMap((f) => f.members), [families]);

  const stats = useMemo(() => {
    let active = 0, noClasses = 0, noPhone = 0;
    for (const s of students) {
      const a = s.active_classes?.length || 0;
      const d = s.dropped_classes?.length || 0;
      if (a > 0) active++;
      if (a === 0 && d === 0) noClasses++;
      if ((s.phones?.length || 0) === 0) noPhone++;
    }
    return { total: students.length, active, noClasses, noPhone };
  }, [students]);

  const expandAll = () => setExpanded(new Set(filtered.map((s) => s.id)));
  const collapseAll = () => setExpanded(new Set());

  return (
    <div className="h-full flex flex-col bg-white" data-testid="students-page">
      <div className="border-b border-slate-200 px-6 py-4 shrink-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-[#D9232E]" /> Students
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {stats.total.toLocaleString()} total · {stats.active.toLocaleString()} enrolled ·{" "}
              {stats.noClasses.toLocaleString()} without classes · {stats.noPhone.toLocaleString()} without phone
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => setNewOpen(true)} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="new-student-button">
              <UserPlus className="w-4 h-4 mr-2" /> New student
            </Button>
            <Button variant="outline" size="sm" onClick={expandAll} data-testid="expand-all-button">Expand all</Button>
            <Button variant="outline" size="sm" onClick={collapseAll} data-testid="collapse-all-button">Collapse all</Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-button">Refresh</Button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by student, phone, or class name..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-10"
              data-testid="student-search-input"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Enrolled" },
              { key: "dropped_only", label: "Dropped only" },
              { key: "no_classes", label: "No classes" },
              { key: "no_phone", label: "No phone" },
            ].map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`px-3 h-9 rounded-md text-xs font-semibold border transition-colors ${
                  filter === f.key
                    ? "bg-[#D9232E] text-white border-[#D9232E]"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading students...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">
            <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            No students match your filters.
          </div>
        ) : (
          <div className="max-w-4xl mx-auto py-2">
            <div className="px-4 pb-2 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
              Showing {families.length.toLocaleString()} famil{families.length === 1 ? "y" : "ies"} · {filtered.length.toLocaleString()} student{filtered.length === 1 ? "" : "s"}
            </div>
            <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
              {families.map((f) => {
                const row = (s) => (
                  <StudentRow
                    key={s.id}
                    s={s}
                    expanded={expanded.has(s.id)}
                    onToggle={() => toggle(s.id)}
                    onRename={renameStudent}
                    onDelete={deleteStudent}
                    onEnroll={enrollInClass}
                    onDrop={(c) => dropFromClass(s, c)}
                    onOpenClass={openClass}
                    onText={openTextForContact}
                    onMove={openMove}
                    onEditPhone={editPhone}
                    siblingSuspects={suspectMap[s.id]}
                    classes={classes}
                  />
                );
                if (f.members.length === 1) return row(f.members[0]);
                return (
                  <FamilyCard key={f.key} family={f} onText={openTextForContact}>
                    {f.members.map(row)}
                  </FamilyCard>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <NewStudentDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        classes={classes}
        contacts={contacts}
        onCreated={load}
      />

      <Dialog open={!!moveTarget} onOpenChange={(v) => { if (!v) { setMoveTarget(null); setMovePhone(""); } }}>
        <DialogContent data-testid="move-student-dialog">
          <DialogHeader>
            <DialogTitle>Move {moveTarget?.name} to a different family</DialogTitle>
            <p className="text-sm text-slate-500">Enter the parent's phone number. If we already have a contact with that phone, this student attaches to it; otherwise a new contact is created.</p>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-xs font-semibold text-slate-700 block">New parent phone *</label>
            <Input
              value={movePhone}
              onChange={(e) => setMovePhone(e.target.value)}
              placeholder="+13615551234"
              data-testid="move-student-phone"
            />
            {moveTarget?.phones?.length > 0 && (
              <div className="text-[11px] text-slate-500">
                Currently on: <b>{moveTarget.phones.map(p => p.phone).join(", ")}</b>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMoveTarget(null)}>Cancel</Button>
            <Button onClick={submitMove} disabled={moveSaving || !movePhone.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="move-student-submit">
              {moveSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Move student"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
